// Category palettes from the design handoff. Known category names get their
// branded colors; unknown ones get a deterministic palette by hashing the name,
// so the same category always reads as the same color across page loads.

export interface Palette {
  primary: string;
  secondary: string;
  surface: string;
  ink: string;
}

const NAMED: Record<string, Palette> = {
  breakfast: { primary: "#F2A65A", secondary: "#E0521E", surface: "#FBE3C2", ink: "#3A2415" },
  salads: { primary: "#7DB069", secondary: "#3D7B4A", surface: "#E2EDB8", ink: "#1F3A1A" },
  salad: { primary: "#7DB069", secondary: "#3D7B4A", surface: "#E2EDB8", ink: "#1F3A1A" },
  pasta: { primary: "#D7444D", secondary: "#9C2F2F", surface: "#F8D5C8", ink: "#3A1612" },
  mains: { primary: "#B0723A", secondary: "#6E3C1F", surface: "#E8C99F", ink: "#3A2412" },
  main: { primary: "#B0723A", secondary: "#6E3C1F", surface: "#E8C99F", ink: "#3A2412" },
  entrees: { primary: "#B0723A", secondary: "#6E3C1F", surface: "#E8C99F", ink: "#3A2412" },
  drinks: { primary: "#3B7BC4", secondary: "#1F4A85", surface: "#C6DEEE", ink: "#142840" },
  beverages: { primary: "#3B7BC4", secondary: "#1F4A85", surface: "#C6DEEE", ink: "#142840" },
  desserts: { primary: "#DC6FA7", secondary: "#9D3D6F", surface: "#F4D0E1", ink: "#3A1A2A" },
  dessert: { primary: "#DC6FA7", secondary: "#9D3D6F", surface: "#F4D0E1", ink: "#3A1A2A" },
  lunch: { primary: "#B0723A", secondary: "#6E3C1F", surface: "#E8C99F", ink: "#3A2412" },
  dinner: { primary: "#9C2F2F", secondary: "#5A1A1A", surface: "#F0C6C0", ink: "#2A0F0F" },
  appetizers: { primary: "#C49A4D", secondary: "#8A6420", surface: "#F2DCB0", ink: "#3A2812" },
  starters: { primary: "#C49A4D", secondary: "#8A6420", surface: "#F2DCB0", ink: "#3A2812" },
  sides: { primary: "#7DB069", secondary: "#3D7B4A", surface: "#E2EDB8", ink: "#1F3A1A" },
};

const FALLBACK: Palette[] = [
  NAMED.breakfast,
  NAMED.salads,
  NAMED.pasta,
  NAMED.mains,
  NAMED.drinks,
  NAMED.desserts,
  { primary: "#5E8C7B", secondary: "#2F5648", surface: "#C7DCD2", ink: "#152823" },
  { primary: "#A37BCB", secondary: "#5E3A8A", surface: "#E5D5F0", ink: "#241A35" },
];

const hash = (s: string): number => {
  const v = (s || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < v.length; i++) h = ((h << 5) - h + v.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const paletteFor = (categoryName: string | undefined): Palette => {
  const key = (categoryName || "").trim().toLowerCase();
  if (key && NAMED[key]) return NAMED[key];
  return FALLBACK[hash(key) % FALLBACK.length];
};
